import React from 'react';
import { X, CheckCircle, BarChart2, Sparkles, MessageCircle } from '../../../icons';
import type { RosterRowRef } from '@discovr/features';
import { useCampaignUiStore } from '@discovr/features';
import { getCreatorAvatarUrl } from '../../utils/campaignDetailHelpers';
import { resolveCreatorPlatformProfile, type CampaignPlatform } from '../../../lib/platformProfile';
import { resolvePlatform } from '@discovr/types';
import { InstagramIcon, YoutubeIcon } from '../../../components/icons';
import { relativeTime } from './utils/formatters';
import { SharePopover } from '../../share/SharePopover';
import { useCampaignShare } from '../../share/useCampaignShare';
import { usePublicView } from '../../publicView';
import type { ShortShareInput } from '../../share/shortLink';
import { CreatorDemographics } from './CreatorDemographics';
import { UnderlineTabNav, type UnderlineTab } from '../../../components/common/UnderlineTabNav';
import { resolvePipeline } from '../../utils/campaignPipeline';

type InsightsTab = 'overview' | 'brand-safety';

const TABS: readonly UnderlineTab<InsightsTab>[] = [
  { key: 'overview', label: 'Creator Analysis', icon: <BarChart2 size={15} /> },
  { key: 'brand-safety', label: 'Brand Safety', icon: <Sparkles size={15} /> },
];

interface HeaderStatsProps {
  slot?: React.ReactNode;
  isLoading?: boolean;
  subscribers: number;
  subscribersLabel?: string;
  ageGroup?: string | null;
  language?: string | null;
  gender?: string | null;
}

/** A slot brings its own container, so it is not boxed again. */
function HeaderStats({
  slot,
  isLoading,
  subscribers,
  subscribersLabel,
  ageGroup,
  language,
  gender,
}: HeaderStatsProps) {
  if (slot) return <div className="mt-4">{slot}</div>;

  return (
    <div className="mt-4 rounded-xl border border-border px-4 py-3">
      <CreatorDemographics
        flush
        withDividers
        isLoading={isLoading}
        subscribers={subscribers}
        subscribersLabel={subscribersLabel}
        ageGroup={ageGroup}
        language={language}
        gender={gender}
      />
    </div>
  );
}

/** Shared round icon-button style for the header (Share / Close / message). */
const ROUND_ICON_BTN =
  'inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E5E5] ' +
  'text-[#737373] transition-colors hover:bg-[#F4F4F2] hover:text-[#171717]';

interface DrawerHeaderProps {
  creator: RosterRowRef;
  activeTab: InsightsTab;
  onTabChange: (tab: InsightsTab) => void;
  onClose: () => void;
  /** Campaign platform — drives the @handle profile link target (YouTube vs Instagram). */
  campaignPlatform?: CampaignPlatform;
  /** Location string from Phyllo (city, state, country). Shown only when available. */
  location?: string;
  /** When true, renders an "InsightIQ Verified" chip next to the handle. Admin-only. */
  isCurated?: boolean;
  /** Phyllo profile.is_protected (IG private account). Admin-only render. */
  isProtected?: boolean;
  /** Phyllo profile.platform_account_type, e.g. "BUSINESS" | "PERSONAL" | "CREATOR".
   *  Admin-only render. Chip is suppressed when value is "PERSONAL" (default). */
  accountType?: string | null;
  /** Phyllo profile.age_group, e.g. "13-17". */
  ageGroup?: string | null;
  /** Phyllo profile.updated_at ISO string. Renders as a small 'Updated Xh ago'
   *  subtitle below the chips. Admin-only. */
  updatedAt?: string | null;
  /** Creator subscriber/follower count (Phyllo) — the Subscribers stat. */
  subscriberCount?: number | null;
  /** Creator content language code(s), e.g. "en" or "en,hi" (Phyllo). */
  language?: string | null;
  /** Creator gender, e.g. "MALE" (Phyllo). */
  gender?: string | null;
  /** First-stat label — "Subscribers" (YouTube) or "Followers" (Instagram). */
  subscribersLabel?: string;
  /** Demographics data hasn't resolved yet — shows 4 skeleton KPI cells. */
  demographicsLoading?: boolean;
  /** Stat strip rendered in place of the Phyllo demographics row. */
  statsSlot?: React.ReactNode;
  /** When true, shows the Approve / Reject / message action cluster (pending creator). */
  canApproveReject?: boolean;
  /** Open the approval flow for this creator. */
  onApprove?: () => void;
  /** Open the reject flow for this creator. */
  onReject?: () => void;
  /** Open the conversation drawer for this creator. */
  onMessage?: () => void;
}

export function DrawerHeader({
  creator,
  activeTab,
  onTabChange,
  onClose,
  campaignPlatform,
  location,
  isCurated,
  isProtected,
  accountType,
  ageGroup,
  updatedAt,
  subscriberCount,
  language,
  gender,
  subscribersLabel,
  demographicsLoading,
  statsSlot,
  canApproveReject,
  onApprove,
  onReject,
  onMessage,
}: DrawerHeaderProps) {
  // Cast to any to read fields that exist on CampaignCreator / RosterEntryFinalized
  // but aren't on the minimal RosterRowRef type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = creator as Record<string, any>;

  // Share deep-link: re-open this creator's insights drawer on the same sub-tab.
  const { canShare, makeShortUrl } = useCampaignShare();
  const { isPublicView } = usePublicView();
  const deepAnalysisTab = useCampaignUiStore((s) => s.deepAnalysisTab);
  const campaignPipeline = useCampaignUiStore((s) => s.campaignPipeline);
  const visibleTabs = TABS.filter(
    (t) => t.key !== 'brand-safety' || resolvePipeline(campaignPipeline).brand_safety_enabled,
  );

  // Platform name and handle mirror exactly what the roster row shows — no fallbacks.
  const displayName = creator.platform.name || '';
  const handle = creator.platform.username;
  const avatarUrl = creator.platform.profile_picture_url || getCreatorAvatarUrl(creator) || null;

  // Share input describing this exact drawer surface — reused for both the
  // active-grant lookup and the mint tail (see SharePopover). `null` when the
  // handle isn't known yet, matching ShareButton's old getUrl-returns-null gate.
  const shareInput: ShortShareInput | null = handle
    ? {
        kind: 'creator',
        username: handle,
        purpose: deepAnalysisTab === 'brand-safety' ? 'safety' : 'insights',
        sub: deepAnalysisTab,
      }
    : null;

  // Social-profile deep link for the @handle (YouTube vs Instagram). This is the
  // link that used to live on the roster's creator column, moved here now that
  // the roster cell opens this drawer instead.
  const { profileUrl } = resolveCreatorPlatformProfile(
    creator as unknown as Record<string, unknown>,
    campaignPlatform,
  );
  // Platform badge shown to the left of the @handle - YouTube vs Instagram.
  const resolvedPlatform = resolvePlatform(campaignPlatform);

  // Subscriber count prefers the Phyllo figure, falling back to the roster snapshot.
  const sm = c.snapshot_metrics as { followers?: number } | undefined;
  const subs = Number(subscriberCount ?? sm?.followers ?? c.followers ?? 0);

  return (
    <div className="shrink-0 bg-muted">
      {/* Header card on the gray drawer. The old top bar (creator name + close)
          is gone — the card's own action row now carries share + close, mirroring
          the Script/Content review drawers. */}
      <div className="px-5 pt-4">
      <div className="rounded-2xl border border-[#E5E5E5] bg-white px-5 pt-5 pb-0">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName || handle || 'Creator'}
              referrerPolicy="no-referrer"
              width={56}
              height={56}
              loading="lazy"
              decoding="async"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="size-14 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-muted text-body-md-semibold text-muted-foreground">
              {(displayName || handle || '').charAt(0).toUpperCase()}
            </div>
          )}

          {/* Identity */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {displayName && (
                <p className="truncate text-heading-h7 text-[#171717]">{displayName}</p>
              )}
              {creator.platform.is_verified && (
                <CheckCircle size={16} className="shrink-0" color="#1D9BF0" aria-label="Verified" />
              )}
            </div>

            {/* Meta line: purple @handle • location • Updated, then admin chips */}
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-caption-medium">
              {handle && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  {resolvedPlatform === 'youtube' ? (
                    <YoutubeIcon size={14} className="shrink-0" />
                  ) : resolvedPlatform === 'instagram' ? (
                    <InstagramIcon size={14} className="shrink-0" />
                  ) : null}
                  {profileUrl ? (
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-[#6C09F7] hover:underline"
                    >
                      @{handle}
                    </a>
                  ) : (
                    <span className="truncate text-[#6C09F7]">@{handle}</span>
                  )}
                </span>
              )}
              {location && (
                <>
                  <span className="text-[#A3A3A3]" aria-hidden>•</span>
                  <span className="truncate text-[#A3A3A3]">{location}</span>
                </>
              )}
              {updatedAt && (
                <>
                  <span className="text-[#A3A3A3]" aria-hidden>•</span>
                  <span className="text-[#A3A3A3]">Updated {relativeTime(updatedAt)}</span>
                </>
              )}
              {isCurated && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-semibold text-emerald-700">
                  ✓ InsightIQ Verified
                </span>
              )}
              {isProtected && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[12px] font-semibold text-foreground">
                  🔒 Private
                </span>
              )}
              {accountType && accountType.toUpperCase() !== 'PERSONAL' && (
                <span className="inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[12px] font-semibold text-indigo-700 capitalize">
                  {accountType.toLowerCase()}
                </span>
              )}
            </p>
          </div>

          {/* Actions — Approve / Reject / message show only for a pending
              creator; share + close always render (order: … message · share ·
              close, matching the Script/Content review drawers). */}
          <div className="flex items-center gap-2 shrink-0">
            {canApproveReject && (
              <>
                <button
                  type="button"
                  onClick={onApprove}
                  className="inline-flex h-9 items-center rounded-full bg-[#18181B] px-5 text-body-sm-medium text-white transition-colors hover:bg-[#27272A]"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={onReject}
                  className="inline-flex h-9 items-center rounded-full border border-[#E5E5E5] bg-white px-5 text-body-sm-medium text-[#171717] transition-colors hover:bg-[#F4F4F2]"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={onMessage}
                  aria-label="Message creator"
                  title="Message creator"
                  className={ROUND_ICON_BTN}
                >
                  <MessageCircle size={16} />
                </button>
              </>
            )}
            {/* `!isPublicView` is belt-and-braces: an anonymous visitor has no
                capabilities set, so `canShare` is already false. Stating it
                explicitly keeps SharePopover's authed useShareGrants query
                unreachable from the public shell by construction rather than by
                the accident of a store default. */}
            {canShare && !isPublicView && (
              <SharePopover
                className={ROUND_ICON_BTN}
                input={shareInput}
                getUrl={() => (shareInput ? makeShortUrl(shareInput) : null)}
              />
            )}
            {/* Public (no-login) share view: the drawer IS the shared surface,
                so there is nothing to close back to — hide the control. */}
            {!isPublicView && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                title="Close"
                className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <HeaderStats
          slot={statsSlot}
          isLoading={demographicsLoading}
          subscribers={subs}
          subscribersLabel={subscribersLabel}
          ageGroup={ageGroup}
          language={language}
          gender={gender}
        />

        {/* Tab switcher — Figma underline tabs (bottom row) */}
        <UnderlineTabNav className="mt-4" tabs={visibleTabs} active={activeTab} onChange={onTabChange} />
      </div>
      </div>
    </div>
  );
}
